/*
  Cart goal free gifts.

  cart-goal.liquid renders which tier is reached and which variant it gives
  away; the cart's actual gift lines are read back from /cart.js rather than
  from the rendered markup, so a gift is always matched by variant id + its
  `_freebie` property. That is what keeps the two in step: exactly one of each
  earned gift, quantity 1, and anything else tagged as a gift is taken out.
*/
(function () {
  const DATA_SELECTOR = 'script[data-cart-freebies]'
  const LOADER_SELECTOR = '[data-cart-freebie-loader]'
  const SECTIONS = ['cart-count-badge', 'offcanvas-cart']
  const MAX_ADDS_PER_TAG = 3

  let busy = false
  // a gift that never comes back tagged would otherwise be added forever
  const addAttempts = {}

  function readTiers() {
    const node = document.querySelector(DATA_SELECTOR)
    if (!node) return null

    try {
      const data = JSON.parse(node.textContent)
      const tiers = Array.isArray(data.tiers) ? data.tiers : []
      return tiers.filter(tier => tier && tier.tag)
    } catch (error) {
      return null
    }
  }

  /*
    `state` is 'adding', 'removing' or 'updating' — a gift leaving the cart is
    as much of a wait as one arriving, so both say what is happening.
  */
  function showLoader(show, state) {
    document.querySelectorAll(LOADER_SELECTOR).forEach(loader => {
      const label = loader.querySelector('[data-cart-freebie-loader-text]')
      const text = loader.dataset[`text${state ? state[0].toUpperCase() + state.slice(1) : 'Adding'}`]

      if (label && text) label.textContent = text

      if (show) {
        loader.removeAttribute('hidden')
      } else {
        loader.setAttribute('hidden', 'hidden')
      }
    })

    document.querySelectorAll('cart-container').forEach(cart => {
      cart.classList.toggle('loading-freebie', show)
    })
  }

  async function getCart() {
    const response = await fetch(`${window.Shopify.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' }
    })
    if (!response.ok) throw new Error('Could not read the cart')
    return response.json()
  }

  async function post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })

    const data = await response.json()
    if (!response.ok) throw new Error(data.description || 'Cart request failed')
    return data
  }

  const addGift = tier =>
    post(`${window.Shopify.routes.cart_add_url}.js`, {
      items: [{ id: tier.variant, quantity: 1, properties: { _freebie: tier.tag } }]
    })

  // by line key, so it survives lines shifting position
  const setQuantity = (key, quantity) =>
    post(`${window.Shopify.routes.cart_change_url}.js`, { id: key, quantity: quantity })

  function giftLines(cart) {
    return (cart.items || [])
      .map(item => ({
        key: item.key,
        variant: item.id,
        quantity: item.quantity,
        tag: (item.properties || {})._freebie
      }))
      .filter(line => line.tag)
  }

  /*
    Everything that has to change, in one list. Removals run before adds so a
    swapped gift (highest-goal-only mode) never briefly doubles up.
  */
  function plan(tiers, cart) {
    const earned = tiers.filter(tier => tier.reached && tier.available && tier.variant)
    const actions = []
    const kept = {}

    giftLines(cart).forEach(line => {
      const tier = earned.find(tier => tier.tag === line.tag && tier.variant === line.variant)

      // not earned any more, pointing at a product that has since changed, or a
      // second copy of a gift already accounted for
      if (!tier || kept[tier.tag]) {
        actions.push({ type: 'remove', key: line.key, tag: line.tag })
        return
      }

      kept[tier.tag] = true

      // a gift is one item, whatever the shopper or an earlier bug made of it
      if (line.quantity !== 1) {
        actions.push({ type: 'quantity', key: line.key, tag: line.tag })
      }
    })

    earned.forEach(tier => {
      if (kept[tier.tag]) {
        addAttempts[tier.tag] = 0
        return
      }
      if ((addAttempts[tier.tag] || 0) >= MAX_ADDS_PER_TAG) return
      actions.push({ type: 'add', tier: tier })
    })

    return actions
  }

  async function repaint() {
    const cart = document.querySelector('cart-container')

    if (!cart || typeof cart.reloadCartElements !== 'function') {
      window.location.reload()
      return
    }

    const url = `${window.location.pathname}?sections=${SECTIONS.join(',')}`
    const response = await fetch(url, { headers: { Accept: 'application/json' } })

    if (!response.ok) {
      window.location.reload()
      return
    }

    cart.reloadCartElements(await response.json())
  }

  async function sync() {
    if (busy) return

    const tiers = readTiers()
    if (!tiers || !tiers.length) return

    busy = true

    try {
      const actions = plan(tiers, await getCart())

      if (!actions.length) {
        busy = false
        showLoader(false)
        return
      }

      const adding = actions.some(action => action.type === 'add')
      const removing = actions.some(action => action.type === 'remove')

      if (adding && removing) {
        showLoader(true, 'updating')
      } else if (adding) {
        showLoader(true, 'adding')
      } else if (removing) {
        showLoader(true, 'removing')
      }

      for (const action of actions) {
        if (action.type === 'add') {
          addAttempts[action.tier.tag] = (addAttempts[action.tier.tag] || 0) + 1
          await addGift(action.tier)
        } else if (action.type === 'remove') {
          await setQuantity(action.key, 0)
        } else {
          await setQuantity(action.key, 1)
        }
      }

      await repaint()
    } catch (error) {
      console.warn('[cart freebies]', error.message)
    } finally {
      busy = false
      showLoader(false)
    }
  }

  // after a re-render only: kt.cart.added / kt.cart.changed fire before the
  // markup — and so the goal state — has been swapped in
  window.addEventListener('kt.cart.reloaded', () => sync())

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync)
  } else {
    sync()
  }
})()
